import { Box, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import FormikControl from "../FormikControl";
import { useDispatch, useSelector } from "react-redux";
import { createOutbondShipement, createShipement, selectVienToVienLoading } from "../../../slices/logisticSlice";
import { useParams } from "react-router";
import { selectCurrentCycle } from "../../../slices/common";
import React, { useEffect, useRef, useMemo, useState } from 'react';
import { fetchAllActiveDeliveryPartners, selectDeliveryActivePartnersAll } from "../../../slices/deliveryPartners";

const initialValues = {
  delivery_partner_id: "",
  awb: "", 
  tracking_link: "",
};

const validationSchema = Yup.object().shape({
  delivery_partner_id: Yup.string().required("Delivery Partner is required"),
  awb: Yup.string() 
    .required("AWB Number is required")
    .max(150, "Maximum of 150 characters"),
  tracking_link: Yup.string()
    .url("Tracking Link must be a valid URL")
    .nullable()
    .notRequired()
    .test(
      "is-empty-or-valid-url",
      "Tracking Link must be a valid URL",
      (value) => !value || Yup.string().url().isValidSync(value)
    ),
});

const MarkAsShippedForm = ({ 
  stepSlug, 
  handleFormSubmit = () => { },
  initialValues: propInitialValues,
  fieldStates = {},
}) => {
  const dispatch = useDispatch();
  const vienToVienLoading = useSelector(selectVienToVienLoading)
  const { feasibilityId } = useParams();
  const currentCycle = useSelector(selectCurrentCycle)
  const activePartners = useSelector(selectDeliveryActivePartnersAll) || [];
  const deliveryPartnerOptions = useMemo(() => {
    return activePartners.map(partner => ({
      ...partner,
      value: partner.value?.toString()
    }));
  }, [activePartners]);
  const renderCount = useRef(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  renderCount.current += 1;
  

  useEffect(() => {
    const fetchAll = async () => {
      await dispatch(fetchAllActiveDeliveryPartners())
    };
    fetchAll();
  }, [dispatch]);

 
  
const formOptions = useMemo(() => {
  return [
    {
      type: "select",
      label: "Delivery Partner",
      name: "delivery_partner_id",
      options: deliveryPartnerOptions,
      required: true,
      placeholder: "Select partner",
      disabled: fieldStates?.delivery_partner_id || false,
    },
    {
      label: "AWB Number",
      type: "text",
      name: "awb",
      required: true,
      placeholder: "Enter AWB number",
      disabled: fieldStates?.awb || false,
    },
    {
      label: "Tracking Link",
      type: "text",
      name: "tracking_link",
      required: false,
      placeholder: "Enter Tracking Link",
      disabled: fieldStates?.tracking_link || false,
    },
  ];
}, [fieldStates, deliveryPartnerOptions]);

  const handleSubmit = async (values) => {
     setIsSubmitting(true);
    try {
      const payload = {
        ...values,
        delivery_partner_id: values.delivery_partner_id && !isNaN(values.delivery_partner_id)
          ? Number(values.delivery_partner_id)
          : values.delivery_partner_id
      };
      if (stepSlug == "product-shipped") {
        const res = await dispatch(
          createOutbondShipement({
            ...payload,
            status: 0,
            ...(fieldStates?.cr_id ? { is_cr: true } : {}),
            feasibility_assessments_id: currentCycle?.feasibility_assessments_id ? currentCycle?.feasibility_assessments_id : +feasibilityId,
            product_manufacturing_id: currentCycle?.product_manufacturing_id,
          })
        ).unwrap();
        if (res.status === 200) {
          handleFormSubmit();
        }
      }
      else {
        const res = await dispatch(
          createShipement({
            ...payload,
            status: 0,
            feasibility_assessments_id: currentCycle?.feasibility_assessments_id ? currentCycle?.feasibility_assessments_id : +feasibilityId,
          })
        ).unwrap();
        if (res.status === 200) {
          handleFormSubmit();
        }
      }
    } catch (err) {
      console.error(err);
    }
     finally {
    setIsSubmitting(false);
  }
  };

  const getInitialValues = () => {
    if (propInitialValues && Object.keys(propInitialValues).length > 0) {
      const processedValues = {
        delivery_partner_id: propInitialValues.delivery_partner_id?.toString() || "",
        awb: propInitialValues.awb || "", 
        tracking_link: propInitialValues.tracking_link || "",
      };
      
      const merged = {
        ...initialValues,
        ...processedValues,
      };
      return merged;
    }
    return initialValues;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });


  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} className="mb-3">
          {formOptions.map((fieldProps, key) => (
            <Grid key={key} item xs={12}>
              <FormikControl 
                formik={formik} 
                {...fieldProps} 
              />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LoadingButton 
              loading={vienToVienLoading ||isSubmitting} 
              fullWidth 
              type="submit" 
              variant="contained"
              disabled={isSubmitting}
            >
              {Object.keys(fieldStates).length > 0 ? "Approve & Update" : "Submit"}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default MarkAsShippedForm;